import {
  Component,
  OnInit,
  ViewChild
} from '@angular/core';
import { SessionService, ApplicationTypeEnum } from '../services/session/shared/session/session-service/session.service';
import { UnityLoanOfficer, UnityBranchOffice } from '../../shared/models/index';
import { Utils } from '../../shared/fork-common-controls/utils.service';
import { MixpanelService } from '../services/mixpanel.service';
import { ConfigurationService } from '../services/configuration/configuration.service';
import { Router } from '@angular/router';

enum FooterTemplateEnum {
  PERSONAL_LOAN_GENERIC = <any>'PL_GENERIC',
  MORTGAGE_GENERIC = <any>'MORT_GENERIC',
  MORTGAGE_BRANCH_OFFICE = <any>'MORT_BO',
  MORTGAGE_LOAN_OFFICER = <any>'MORT_LO',
  DASHBOARD = <any>'DASHBOARD',
  LOGIN = <any>'LOGIN'
}

@Component({
  selector: 'ld-footer',
  templateUrl: './footer.component.html',
  styleUrls: [ './footer.style.scss' ]
})

export class FooterComponent implements OnInit {
  public showMobileMenu = false;
  public loanOfficer: UnityLoanOfficer;
  public loanOfficerBranch: UnityBranchOffice;
  public applicationType: ApplicationTypeEnum;
  public branchOffice: UnityBranchOffice;
  public disclosureContent = '';
  public footerTemplateEnums = FooterTemplateEnum;
  public footerTemplate: FooterTemplateEnum = null;
  public disclosureToShow: string = null;

  private plLegalCopyIsActive = false;

  @ViewChild('emailPrompt') private emailModal;

  constructor(
    private configService: ConfigurationService,
    public mixpanelService: MixpanelService,
    private router: Router,
    private sessionService: SessionService,
    private utils: Utils) {
  }

  public ngOnInit(): void {
    this.sessionService.onApplicationTypeChange().subscribe(this.handleApplicationTypeChange, console.error);
    this.router.events.subscribe(() => {

      // Only make fetch the PL Legal Copy on route navigation if the PL disclaimer tab is active.
      if (this.plLegalCopyIsActive) {
        this.configService.getPlLoanLegalCopy().subscribe(legalCopy => this.disclosureContent = legalCopy);
      }
    });
  }

  private get phoneNumber(): string {
    const rawPhoneNumber = this.utils.getPhoneNumber(this.loanOfficer, this.loanOfficerBranch, this.branchOffice);
    return Utils.formatPhoneNumber(rawPhoneNumber);
  }

  public get isPersonalLoan(): boolean {
    return (this.applicationType === ApplicationTypeEnum.PersonalLoans);
  }

  private get isDashboard(): boolean {
    return this.applicationType === ApplicationTypeEnum.Dashboard;
  }

  private get isLogin(): boolean {
    return this.applicationType === ApplicationTypeEnum.Login;
  }

  private setFooterTemplate() {
    if (this.isPersonalLoan) {
      this.footerTemplate = FooterTemplateEnum.PERSONAL_LOAN_GENERIC;
    } else if (this.isDashboard) {
      this.footerTemplate = FooterTemplateEnum.DASHBOARD;
    } else if (this.isLogin) {
      this.footerTemplate = FooterTemplateEnum.LOGIN;
    } else {
      this.footerTemplate = null;
    }
  }

  private setDisclosureContent(selectedDisclosure = '') {
    this.plLegalCopyIsActive = false;

    switch (selectedDisclosure) {
      case 'Lifetime Guarantee':
        this.disclosureContent = `
        loanDepot Lifetime Guarantee ("Guarantee") — Guarantee applies to future refinances of the same property by the same
        borrower subject to the conditions and contact requirements outlined below. This Guarantee is non-transferable. This Guarantee may
        not be applied to a Purchase Loan, a Home Equity Loan, a Renovation Loan, a Personal Loan, or combined with other discounts or
        promotions. This Guarantee excludes down payment assistance programs, bond loan programs, and any loans received by loanDepot from
        a third party loan originator (e.g., wholesale or brokered transactions). Future refinancing is conditional on whether the program
        type or applicable state/federal laws contain "seasoning" restrictions that would make refinancing during a specific period legal.
        For example, waiting until after the 12th month may be required. For future refinances of the same property with loanDepot, this
        Guarantee may not be redeemed within 180 days of the Date Issued. Your loan approval for any future loan is not guaranteed. The
        appraised value of your home for the future loan is not guaranteed. In the event a full appraisal of your home is required, the
        property value at that time must be acceptable for the program and loan terms requested. The interest rate for the future loan is
        not guaranteed. You will have to qualify for the loan requested and will be quoted interest rates in effect at the time of your
        next loan. For loans meeting the above requirements and which are successfully closed, your appraisal fee will be reimbursed at the
        closing of the loan transaction and no lender fees will be charged. "Lender fees" do not include discount points associated with
        your loan transaction. "Appraisal fee" includes only the fee associated with the first appraisal on your loan transaction and does
        not include any ancillary appraisal fees that may be charged in connection with your loan transaction (e.g., second appraisal fee,
        appraisal re-inspection fee, appraisal recertification fee, etc.). This program is only available to applicants who closed their
        previous loan with loanDepot and received a "loanDepot Lifetime Guarantee" certificate.`;

        break;

      case 'Disclaimers':
        this.disclosureContent = `
        Testimonial disclosure - Compensation was not paid in exchange for any testimonial on this site. Individuals pictured may
        not be that of the endorsers and is for display purposes only.`;

        break;

      case 'Personal Loans':
        this.plLegalCopyIsActive = true;
        this.configService.getPlLoanLegalCopy().subscribe(legalCopy => this.disclosureContent = legalCopy);
        break;

      default:
        this.disclosureContent = '';
    }
  }

  handleApplicationTypeChange = (type: ApplicationTypeEnum) => {
    this.applicationType = type;
    this.setFooterTemplate();
  }

  toggleQuestions(): void {
    this.showMobileMenu = !this.showMobileMenu;
  }

  toggleSelected(selected: string): void {
    this.disclosureToShow = selected;
    this.setDisclosureContent(selected);
  }

  fireflyKeypress(event): void {
    switch (event.key) {
      case('Tab'):
        return;
      default:
        event.preventDefault();
        this.fireflyLaunch(event);
    }
  }

  fireflyLaunch(event): void {
    if (!window) {
      return;
    } else {
      const firefly: any = window['fireflyAPI'];
      firefly.ready(() => {
        firefly.startAPI(firefly.token);
      });
      event.target.blur();

      let analyticsObject: any = {
        Definition: 'Borrower clicks Share My Screen',
        Product: 'U',
        User: 'B',
        Experience: 'E',
        'Session Code': firefly.token
      };

      this.mixpanelService.track('Co-Browse Requested UBE', analyticsObject);
    }
  }

  emailModalKeypress(event): void {
    switch (event.key) {
      case('Tab'):
        return;
      default:
        event.preventDefault();
        this.showEmailModal();
    }
  }

  showEmailModal(): void {
    this.emailModal.show();
  }

  // TODO: Nathan Walston wants this
  // this.footerTemplate = FooterTemplateEnum.MORTGAGE_BRANCH_OFFICE;
  //
  // this.branchOffice = {
  //   title: 'Wizarding Branch of New England',
  //   phoneNumber: '0987654321',
  //   mailAddress: 'wizardschool@gov.edu.co.uk',
  //   address: {
  //     addressLine1: '12 34th St',
  //     addressLine2: '#567',
  //     city: 'Xyzzyx',
  //     state: 'CA',
  //     zipCode: '99999-9999'
  //   },
  // } as any;
  //
  // this.loanOfficer = {
  //   firstName: 'Merlin',
  //   lastName: 'of Camelot',
  //   title: 'Wizard',
  //   license: 'WZRD 12345-67890',
  //   emailAddress: 'MerlinOfCamelotTheMagnificent@scotlandyard.co.uk',
  //   address: {
  //     addressLine1: '12 34th St',
  //     addressLine2: '#567',
  //     city: 'Xyzzyx',
  //     state: 'CA',
  //     zipCode: '99999-9999'
  //   },
  // } as any;
  //
  // this.loanOfficerBranch = {
  //   address: {
  //     addressLine1: '12 34th St',
  //     addressLine2: '#567',
  //     city: 'Xyzzyx',
  //     state: 'CA',
  //     zipCode: '99999-9999'
  //   }
  // };
}
